import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getMeasure, getReport, analyzeMeasure, getLinkages, getConflicts, getFinance, ingestFinance, getAuthors } from "@/lib/api";
import { ConfidenceStamp, Quote } from "@/components/Evidence";
import { QuoteContextProvider } from "@/components/QuoteContextProvider";
import RiskScorecard from "@/components/RiskScorecard";
import DiffView from "@/components/DiffView";
import TransparencyFooter from "@/components/TransparencyFooter";
import HiddenCostCard from "@/components/HiddenCostCard";
import RealityCheckCard from "@/components/RealityCheckCard";
import PeopleAreSayingCard from "@/components/PeopleAreSayingCard";
import HowToReadReport from "@/components/HowToReadReport";
import {
  ArrowLeft, CheckCircle2, XCircle, Loader2, RefreshCw, ExternalLink,
  FileWarning, Link2, Scale, DollarSign, FileText, AlertOctagon, ScanSearch,
  GitCompare, Plus, ShieldAlert, Users, Info
} from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 4000;
const ACTIVE_STATUSES = ["ingesting", "analyzing"];

export default function MeasureReportPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [legalActions, setLegalActions] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const loadAll = async () => {
    try {
      const m = await getMeasure(id);
      setData(m);
      if (m?.report) {
        const full = await getReport(id).catch(() => null);
        if (full?.report) setReport(full.report);
        if (full?.legal_actions) setLegalActions(full.legal_actions);
        if (full?.findings) setFindings(full.findings);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!data?.measure) return;
    if (!ACTIVE_STATUSES.includes(data.measure.status)) return;
    const t = setInterval(loadAll, POLL_MS);
    return () => clearInterval(t);
  }, [data?.measure?.status]); // eslint-disable-line

  const handleReanalyze = async () => {
    setAnalyzing(true);
    try {
      await analyzeMeasure(id, { force: true });
      toast.success("Re-analysis started");
      setTimeout(loadAll, 800);
    } catch (e) {
      toast.error("Failed to start analysis");
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-5 py-16 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-[hsl(var(--ink-muted))]" />
        <div className="text-sm text-[hsl(var(--ink-muted))]">Loading measure…</div>
      </div>
    );
  }

  if (!data?.measure) {
    return (
      <div className="max-w-xl mx-auto px-5 py-16 text-center" data-testid="measure-not-found">
        <div className="paper-card-elevated rounded-sm p-8">
          <AlertOctagon className="w-7 h-7 mx-auto mb-3 text-[hsl(var(--stamp-red))]" />
          <div className="font-serif text-2xl mb-2">Measure not found.</div>
          <div className="text-sm text-[hsl(var(--ink-muted))] mb-1">
            The id below is not in our dossier. It may have been deleted,
            or the analysis may still be starting.
          </div>
          <code className="inline-block text-[11px] font-mono text-[hsl(var(--ink-muted))] bg-[hsl(var(--paper-2))] px-2 py-0.5 rounded-sm mt-3 break-all">
            {id}
          </code>
          <div className="flex items-center justify-center gap-2 flex-wrap mt-6">
            <button
              onClick={() => { setLoading(true); loadAll(); }}
              className="pill-btn pill-btn-ghost"
              data-testid="not-found-retry"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
            <Link to="/measures" className="pill-btn pill-btn-ghost" data-testid="not-found-list">
              <FileText className="w-3.5 h-3.5" /> All measures
            </Link>
            <Link to="/" className="pill-btn pill-btn-primary" data-testid="not-found-home">
              Back home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const m = data.measure;
  const sources = data.sources || [];
  const isActive = ACTIVE_STATUSES.includes(m.status);
  const hasReport = !!report;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="report-page">
      <Link to="/measures" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))] mb-5">
        <ArrowLeft className="w-3 h-3" /> all measures
      </Link>

      <header className="mb-6 pb-5 border-b border-[hsl(var(--rule))]" data-testid="report-header">
        <div className="flex items-center gap-2 mb-2">
          <span className="section-label">{m.jurisdiction || "CA"}</span>
          {m.election_date && (
            <span className="text-[11px] font-mono text-[hsl(var(--ink-muted))]">
              · {m.election_date}
            </span>
          )}
          <StatusBadge status={m.status} message={m.status_message} />
        </div>
        <h1 className="hero-title text-3xl sm:text-4xl mb-2">{m.title || m.measure_id}</h1>
        <div className="text-sm font-mono text-[hsl(var(--ink-muted))]">{m.measure_id}</div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button
            onClick={handleReanalyze}
            disabled={analyzing || isActive}
            data-testid="reanalyze-btn"
            className="pill-btn pill-btn-ghost disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${analyzing ? "animate-spin" : ""}`} />
            Re-run analysis
          </button>
          <span className="text-[11px] text-[hsl(var(--ink-muted))] font-mono">
            {sources.length} source{sources.length !== 1 ? "s" : ""} · {findings.length} findings
          </span>
        </div>
      </header>

      <BillContextBanner measureIdLabel={m.measure_id} />
      <AuthorsBanner measureDbId={m.id} measureIdLabel={m.measure_id} />

      {isActive && (
        <div className="paper-card p-6 mb-6 text-center" data-testid="analyzing-state">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[hsl(var(--accent-blue))]" />
          <div className="text-sm">{m.status_message || "Processing…"}</div>
          <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">This page will update automatically.</div>
        </div>
      )}

      {m.status === "error" && (
        <div className="paper-card p-5 mb-6 border-l-4 border-[hsl(var(--stamp-red))]" data-testid="error-state">
          <div className="flex items-start gap-3">
            <AlertOctagon className="w-5 h-5 text-[hsl(var(--stamp-red))] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">Analysis error</div>
              <div className="text-sm text-[hsl(var(--ink-2))] font-mono">{m.status_message}</div>
            </div>
          </div>
        </div>
      )}

      {hasReport ? (
        <QuoteContextProvider measureDbId={m.id}>
          <ReportTabs
            measureDbId={m.id}
            report={report}
            legalActions={legalActions}
            findings={findings}
            sources={sources}
          />
        </QuoteContextProvider>
      ) : !isActive && m.status !== "error" ? (
        <div className="paper-card p-6 text-center text-sm text-[hsl(var(--ink-muted))]">
          No report yet. Click &ldquo;Re-run analysis&rdquo; above.
        </div>
      ) : null}

      <TransparencyFooter />
    </div>
  );
}

function StatusBadge({ status, message }) {
  const map = {
    ingesting: { cls: "stamp-implied", label: "Ingesting" },
    ingested: { cls: "stamp-implied", label: "Ingested" },
    analyzing: { cls: "stamp-implied", label: "Analyzing" },
    analyzed: { cls: "stamp-explicit", label: "Analyzed" },
    error: { cls: "stamp-uncertain", label: "Error" },
  };
  const meta = map[status] || { cls: "stamp-inferred", label: status };
  return (
    <span className={`stamp ${meta.cls}`} title={message || ""} data-testid={`status-${status}`}>
      {meta.label}
    </span>
  );
}

function ReportTabs({ measureDbId, report, legalActions, findings, sources }) {
  const peopleRef = React.useRef(null);
  return (
    <>
      <HowToReadReport />
      <HiddenCostCard
        findings={report.hidden_cost_findings || []}
        renderCard={!!report.hidden_cost_render_card}
        emptyState={report.hidden_cost_empty_state || ""}
      />
      <PeopleAreSayingCard ref={peopleRef} measureDbId={measureDbId} />
      <RealityCheckCard
        measureDbId={measureDbId}
        onChecked={() => peopleRef.current?.refresh?.()}
      />
      <Tabs defaultValue="summary" className="w-full" data-testid="report-tabs">
      <TabsList className="w-full overflow-x-auto flex-nowrap justify-start bg-[hsl(var(--paper-2))] h-auto p-1">
        {[
          ["summary", "Summary", FileText],
          ["changes", "Changes to Current Law", Scale],
          ["hidden", "Hidden Stuff", FileWarning],
          ["power", "Who Gains Power", ScanSearch],
          ["conflicts", "Conflicts", GitCompare],
          ["money", "Money & Influence", DollarSign],
          ["linkages", "Linkages", Link2],
          ["evidence", "Evidence", FileText],
        ].map(([v, label, Icon]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="gap-1.5 whitespace-nowrap">
            <Icon className="w-3.5 h-3.5" />
            <span className="text-xs sm:text-sm">{label}</span>
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="summary" className="mt-5">
        <SummaryTab report={report} />
      </TabsContent>
      <TabsContent value="changes" className="mt-5">
        <ChangesTab report={report} legalActions={legalActions} />
      </TabsContent>
      <TabsContent value="hidden" className="mt-5">
        <HiddenTab report={report} />
      </TabsContent>
      <TabsContent value="power" className="mt-5">
        <PowerTab report={report} />
      </TabsContent>
      <TabsContent value="conflicts" className="mt-5">
        <ConflictsTab measureDbId={measureDbId} />
      </TabsContent>
      <TabsContent value="money" className="mt-5">
        <MoneyTab measureDbId={measureDbId} />
      </TabsContent>
      <TabsContent value="linkages" className="mt-5">
        <LinkagesTab report={report} measureDbId={measureDbId} />
      </TabsContent>
      <TabsContent value="evidence" className="mt-5">
        <EvidenceTab findings={findings} sources={sources} />
      </TabsContent>
    </Tabs>
    </>
  );
}

// ---------- Tabs ---------- //

function SummaryTab({ report }) {
  const lens = report.contract_reviewer_lens || {};
  const hasLens =
    lens && (lens.red_team_read || lens.headline_vs_text_gap ||
      (lens.catalyst_hypothesis || []).length > 0 ||
      (lens.activities_made_harder || []).length > 0 ||
      (lens.activities_made_easier || []).length > 0 ||
      (lens.likely_beneficiaries || []).length > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="summary-tab">
      <div className="lg:col-span-2 space-y-5">
        <section className="paper-card-elevated p-5 sm:p-6 rounded-sm">
          <div className="section-label mb-2">Plain English</div>
          <p className="font-serif text-xl sm:text-[1.375rem] leading-relaxed text-[hsl(var(--ink))]">
            {report.plain_english_summary || "No summary generated."}
          </p>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <VoteCard
            kind="yes"
            text={report.yes_vote_effect}
            icon={CheckCircle2}
            color="hsl(var(--accent-green))"
          />
          <VoteCard
            kind="no"
            text={report.no_vote_effect}
            icon={XCircle}
            color="hsl(var(--stamp-red))"
          />
        </div>

        {hasLens && <ContractReviewerCard lens={lens} />}

        <section className="paper-card p-5 rounded-sm" data-testid="top-effects">
          <div className="section-label mb-3">Top practical effects</div>
          <ul className="space-y-3">
            {(report.top_practical_effects || []).map((e, i) => (
              <li key={i} className="pb-3 border-b border-dashed border-[hsl(var(--rule))] last:border-0 last:pb-0">
                <div className="flex items-start gap-2 mb-1.5">
                  <span className="font-mono text-[11px] text-[hsl(var(--ink-muted))] mt-1 shrink-0">0{i + 1}</span>
                  <div className="flex-1">
                    <div className="text-[15px] leading-snug font-medium">{e.effect}</div>
                  </div>
                  <ConfidenceStamp
                    confidence={e.inferred ? "strongly_implied" : "explicit"}
                    inferred={e.inferred}
                  />
                </div>
                {e.quote && <Quote text={e.quote} className="ml-6" />}
              </li>
            ))}
            {(!report.top_practical_effects || report.top_practical_effects.length === 0) && (
              <li className="text-sm text-[hsl(var(--ink-muted))]">None extracted.</li>
            )}
          </ul>
        </section>
      </div>

      <aside className="space-y-5">
        <section className="paper-card p-4 rounded-sm">
          <div className="section-label mb-3">Risk scorecard</div>
          <RiskScorecard riskLabels={report.risk_labels} />
        </section>

        {report.uncertainties && report.uncertainties.length > 0 && (
          <section className="paper-card p-4 rounded-sm border-l-4 border-[hsl(var(--accent-amber))]" data-testid="uncertainties">
            <div className="section-label mb-2 text-[hsl(var(--accent-amber))]">Unknowns</div>
            <ul className="space-y-1.5 text-sm text-[hsl(var(--ink-2))]">
              {report.uncertainties.map((u, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[hsl(var(--accent-amber))]">—</span>
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </aside>
    </div>
  );
}

function ContractReviewerCard({ lens }) {
  const catalysts = lens.catalyst_hypothesis || [];
  const harder = lens.activities_made_harder || [];
  const easier = lens.activities_made_easier || [];
  const benef = lens.likely_beneficiaries || [];
  return (
    <section
      className="paper-card-elevated rounded-sm p-5 sm:p-6"
      style={{ borderLeft: "3px solid hsl(var(--stamp-red))" }}
      data-testid="contract-reviewer-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="section-label mb-1" style={{ color: "hsl(var(--stamp-red))" }}>
            Hostile contract reviewer · adversarial read
          </div>
          <div className="text-[11px] text-[hsl(var(--ink-muted))] font-mono">
            What the text structurally does, beyond how it is sold. Inference, grounded in quotes.
          </div>
        </div>
        <span className="stamp stamp-uncertain">Inference</span>
      </div>

      {lens.red_team_read && (
        <p className="font-serif text-lg leading-relaxed mb-4" data-testid="red-team-read">
          {lens.red_team_read}
        </p>
      )}

      {lens.headline_vs_text_gap && (
        <div className="mb-4 pb-3 border-b border-dashed border-[hsl(var(--rule))]">
          <div className="section-label mb-1.5">Headline vs. text gap</div>
          <p className="text-sm text-[hsl(var(--ink-2))] leading-relaxed">
            {lens.headline_vs_text_gap}
          </p>
          {lens.stated_purpose && (
            <p className="text-[11px] font-mono text-[hsl(var(--ink-muted))] mt-2">
              As sold: “{lens.stated_purpose.slice(0, 200)}”
            </p>
          )}
        </div>
      )}

      {catalysts.length > 0 && (
        <div className="mb-4">
          <div className="section-label mb-2">Catalyst hypotheses</div>
          <ul className="space-y-3">
            {catalysts.map((c, i) => (
              <li key={i} className="pb-2" data-testid={`catalyst-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                  <div className="font-medium text-[15px] leading-snug flex-1">
                    {c.hypothesis}
                  </div>
                  <ConfidenceStamp
                    confidence={c.confidence || "plausible_uncertain"}
                    inferred={c.inferred !== false}
                  />
                </div>
                {c.supporting_signal && (
                  <div className="text-xs text-[hsl(var(--ink-muted))] mb-1">
                    <span className="font-mono uppercase tracking-wider">signal · </span>
                    {c.supporting_signal}
                  </div>
                )}
                {c.quote && <Quote text={c.quote} />}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {harder.length > 0 && (
          <div
            className="paper-card p-3 rounded-sm"
            style={{ borderTop: "3px solid hsl(var(--stamp-red))" }}
            data-testid="activities-harder"
          >
            <div className="text-[11px] font-mono uppercase tracking-widest text-[hsl(var(--stamp-red))] mb-2">
              Made harder / chilled
            </div>
            <ul className="space-y-2">
              {harder.map((a, i) => (
                <li key={i} className="text-sm">
                  <div className="font-medium">{a.activity}</div>
                  {a.mechanism && (
                    <div className="text-xs text-[hsl(var(--ink-muted))]">{a.mechanism}</div>
                  )}
                  {a.quote && (
                    <div className="quote-block text-[12px] mt-1">“{a.quote}”</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {easier.length > 0 && (
          <div
            className="paper-card p-3 rounded-sm"
            style={{ borderTop: "3px solid hsl(var(--accent-green))" }}
            data-testid="activities-easier"
          >
            <div className="text-[11px] font-mono uppercase tracking-widest text-[hsl(var(--accent-green))] mb-2">
              Made easier / shielded
            </div>
            <ul className="space-y-2">
              {easier.map((a, i) => (
                <li key={i} className="text-sm">
                  <div className="font-medium">{a.activity}</div>
                  {a.mechanism && (
                    <div className="text-xs text-[hsl(var(--ink-muted))]">{a.mechanism}</div>
                  )}
                  {a.quote && (
                    <div className="quote-block text-[12px] mt-1">“{a.quote}”</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {benef.length > 0 && (
        <div>
          <div className="section-label mb-2">Likely beneficiaries</div>
          <ul className="space-y-2">
            {benef.map((b, i) => (
              <li key={i} className="text-sm" data-testid={`beneficiary-${i}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <span className="font-medium">{b.beneficiary}</span>
                  <ConfidenceStamp
                    confidence={b.confidence || "plausible_uncertain"}
                    inferred={b.inferred !== false}
                  />
                </div>
                {b.why && <div className="text-xs text-[hsl(var(--ink-muted))] mt-0.5">{b.why}</div>}
                {b.quote && <Quote text={b.quote} className="mt-1.5" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function BillContextBanner({ measureIdLabel }) {
  const isBillish = /^(AB|SB|ACA|SCA|ACR|SCR|AJR|SJR)\b/.test(measureIdLabel || "");
  if (!isBillish) return null;
  return (
    <section
      className="paper-card rounded-sm p-4 mb-4 flex items-start gap-3"
      data-testid="bill-context-banner"
      style={{ borderLeft: "3px solid hsl(var(--accent-blue))" }}
    >
      <Info className="w-4 h-4 mt-0.5 text-[hsl(var(--accent-blue))] shrink-0" />
      <div className="text-sm text-[hsl(var(--ink-2))] leading-relaxed">
        <strong className="text-[hsl(var(--ink))]">This bill won&rsquo;t appear on your ballot.</strong>{" "}
        It&rsquo;s voted on by your Assemblymembers and Senators &mdash; not directly by voters.
        This page shows you what&rsquo;s actually in the bill, who&rsquo;s pushing it, and how to
        track what your representatives do with it. Bills can become law, get killed in committee,
        or trigger a citizen&rsquo;s referendum that does end up on your ballot.
      </div>
    </section>
  );
}


function AuthorsBanner({ measureDbId, measureIdLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getAuthors(measureDbId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [measureDbId]);

  // Only show for Assembly/Senate bills — propositions won't have authors in this format
  const isBillish = /^(AB|SB|ACA|SCA|ACR|SCR|AJR|SJR)\b/.test(measureIdLabel || "");
  if (!isBillish) return null;

  if (loading) {
    return (
      <section className="paper-card p-4 rounded-sm mb-6" data-testid="authors-banner-loading">
        <div className="flex items-center gap-2 text-[hsl(var(--ink-muted))]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span className="text-xs font-mono uppercase tracking-wider">Resolving authors &amp; sponsors…</span>
        </div>
      </section>
    );
  }

  const authors = data?.authors || [];
  if (authors.length === 0) return null;

  // Group: committee entries separate from individuals; lead/principal first, then coauthors
  const committees = authors.filter((a) => a.is_committee);
  const roleRank = (r) =>
    r === "lead" ? 0 :
    r === "principal_coauthor" ? 1 :
    r === "coauthor" ? 2 :
    r?.startsWith("committee_member") ? 3 : 4;
  const individuals = authors
    .filter((a) => !a.is_committee)
    .sort((a, b) => roleRank(a.role) - roleRank(b.role));

  const leads = individuals.filter((a) => a.role === "lead" || a.role === "principal_coauthor");
  const coauthors = individuals.filter((a) => a.role === "coauthor");
  const committeeMembers = individuals.filter((a) => (a.role || "").startsWith("committee_member"));
  const initialShown = expanded ? individuals.length : 8;

  return (
    <section
      className="paper-card-elevated p-5 rounded-sm mb-6"
      style={{ borderLeft: "3px solid hsl(var(--accent-blue))" }}
      data-testid="authors-banner"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4 pb-3 border-b border-dashed border-[hsl(var(--rule))]">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[hsl(var(--accent-blue))]" />
          <div>
            <div className="section-label" style={{ color: "hsl(var(--accent-blue))" }}>
              Authors &amp; sponsors
            </div>
            <div className="text-[11px] text-[hsl(var(--ink-muted))] mt-0.5">
              Pulled from leginfo. Party &amp; district matched to current CA legislator roster.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-mono text-[hsl(var(--ink-muted))]">
          {leads.length > 0 && <span className="stamp stamp-explicit">{leads.length} lead{leads.length !== 1 ? "s" : ""}</span>}
          {coauthors.length > 0 && <span className="stamp stamp-implied">{coauthors.length} coauthor{coauthors.length !== 1 ? "s" : ""}</span>}
          {committees.length > 0 && <span className="stamp stamp-inferred">committee-authored</span>}
        </div>
      </div>

      {committees.length > 0 && (
        <div className="mb-4 pb-3 border-b border-dashed border-[hsl(var(--rule))]">
          {committees.map((c, i) => (
            <div key={i} className="flex items-baseline gap-2 flex-wrap" data-testid={`author-committee-${i}`}>
              <span className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                Authored by
              </span>
              <span className="font-serif text-lg font-semibold">{c.name}</span>
              <span className="stamp stamp-inferred">{c.chamber}</span>
            </div>
          ))}
        </div>
      )}

      {leads.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mb-2">
            Lead author{leads.length !== 1 ? "s" : ""}
          </div>
          <div className="flex flex-wrap gap-2">
            {leads.map((a, i) => (
              <AuthorChip key={i} author={a} prominent index={i} testidPrefix="author-lead" />
            ))}
          </div>
        </div>
      )}

      {coauthors.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mb-2">
            Coauthor{coauthors.length !== 1 ? "s" : ""}
          </div>
          <div className="flex flex-wrap gap-2">
            {coauthors.slice(0, expanded ? coauthors.length : 12).map((a, i) => (
              <AuthorChip key={i} author={a} index={i} testidPrefix="author-coauthor" />
            ))}
            {!expanded && coauthors.length > 12 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 border border-[hsl(var(--rule))] rounded-sm text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))]"
                data-testid="authors-show-more"
              >
                + {coauthors.length - 12} more
              </button>
            )}
          </div>
        </div>
      )}

      {committeeMembers.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mb-2">
            Committee members
          </div>
          <div className="flex flex-wrap gap-2">
            {committeeMembers.slice(0, expanded ? committeeMembers.length : 12).map((a, i) => (
              <AuthorChip key={i} author={a} compact index={i} testidPrefix="author-committee-member" />
            ))}
            {!expanded && committeeMembers.length > 12 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 border border-[hsl(var(--rule))] rounded-sm text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))]"
                data-testid="authors-show-more-committee"
              >
                + {committeeMembers.length - 12} more
              </button>
            )}
          </div>
        </div>
      )}

      <div className="pt-3 border-t border-dashed border-[hsl(var(--rule))] text-[11px] text-[hsl(var(--ink-muted))] leading-snug">
        Affiliation (party + district) pulled from{" "}
        <a href="https://www.senate.ca.gov/senators" target="_blank" rel="noreferrer" className="underline hover:text-[hsl(var(--ink))]">senate.ca.gov</a>
        {" / "}
        <a href="https://www.assembly.ca.gov/assemblymembers" target="_blank" rel="noreferrer" className="underline hover:text-[hsl(var(--ink))]">assembly.ca.gov</a>.
        Click any author to open Cal-Access &amp; FPPC funder pages in a new tab.
        {initialShown < individuals.length && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="ml-2 underline hover:text-[hsl(var(--ink))]"
            data-testid="authors-banner-expand-all"
          >
            Show all
          </button>
        )}
      </div>
    </section>
  );
}

function AuthorChip({ author, prominent, compact, index, testidPrefix }) {
  const party = (author.party || "").toUpperCase();
  const dist = author.district_code || "";
  const fullName = author.full_name || author.name;
  // Party color
  const partyColor =
    party === "D" ? "hsl(220 70% 40%)" :
    party === "R" ? "hsl(0 70% 42%)" :
    party === "I" ? "hsl(45 85% 40%)" :
    "hsl(var(--ink-muted))";

  const base = prominent
    ? "paper-card-elevated p-3 rounded-sm"
    : "border border-[hsl(var(--rule))] rounded-sm";
  const pad = prominent ? "" : compact ? "px-2.5 py-1.5" : "px-3 py-2";

  // Primary outgoing link: Cal-Access power search
  const href = author.links?.cal_access_power_search || "#";

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`group inline-flex items-center gap-2 ${base} ${pad} hover:border-[hsl(var(--accent-blue))] transition-colors`}
      style={prominent ? { borderLeft: `3px solid ${partyColor}` } : undefined}
      data-testid={`${testidPrefix}-${index}`}
      title={`Open Cal-Access funder search for ${fullName}`}
    >
      {prominent && (
        <span
          className="font-mono text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm"
          style={{ color: partyColor, backgroundColor: `${partyColor.replace(")", " / 0.08)")}` }}
          data-testid={`${testidPrefix}-party-${index}`}
        >
          {party || "?"}
        </span>
      )}
      <span className={prominent ? "font-serif text-[1rem] font-semibold" : "text-sm font-medium"}>
        {fullName}
      </span>
      {!prominent && party && (
        <span
          className="font-mono text-[10px] font-bold px-1 rounded-sm"
          style={{ color: partyColor }}
        >
          ({party})
        </span>
      )}
      {dist && (
        <span className="font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--ink-muted))]">
          {dist}
        </span>
      )}
      {!dist && author.chamber && (
        <span className="font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--ink-muted))]">
          {author.chamber}
        </span>
      )}
      <ExternalLink className="w-2.5 h-2.5 opacity-40 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}

function AuthorsCard({ measureDbId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getAuthors(measureDbId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [measureDbId]);

  if (loading) {
    return (
      <section className="paper-card p-4 rounded-sm" data-testid="authors-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
      </section>
    );
  }
  if (!data || !data.authors || data.authors.length === 0) return null;

  return (
    <section
      className="paper-card p-4 rounded-sm"
      style={{ borderLeft: "3px solid hsl(var(--accent-blue))" }}
      data-testid="authors-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-3.5 h-3.5 text-[hsl(var(--accent-blue))]" />
        <span className="section-label">Authors &amp; funders</span>
      </div>

      <ul className="space-y-3">
        {data.authors.map((a, i) => (
          <li key={i} className="pb-3 border-b border-dashed border-[hsl(var(--rule))] last:border-0 last:pb-0" data-testid={`author-${i}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="font-serif text-[1.05rem] font-semibold">{a.name}</span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                {a.role}{a.chamber ? ` · ${a.chamber}` : ""}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <a
                href={a.links.cal_access_power_search}
                target="_blank"
                rel="noreferrer"
                className="evidence-pill"
                data-testid={`author-powersearch-${i}`}
                title="Search Cal-Access for campaign contributions"
              >
                Cal-Access funders <ExternalLink className="w-2.5 h-2.5" />
              </a>
              <a
                href={a.links.cal_access_candidate_search}
                target="_blank"
                rel="noreferrer"
                className="evidence-pill"
                data-testid={`author-candidate-${i}`}
                title="Candidate filings on Cal-Access"
              >
                Candidate filings <ExternalLink className="w-2.5 h-2.5" />
              </a>
              <a
                href={a.links.leginfo_member_search}
                target="_blank"
                rel="noreferrer"
                className="evidence-pill"
                data-testid={`author-leginfo-${i}`}
                title="Find on California legislature roster"
              >
                Leginfo member ↗
              </a>
              <a
                href={a.links.google_contributors}
                target="_blank"
                rel="noreferrer"
                className="evidence-pill"
                data-testid={`author-google-${i}`}
                title="Broader web search of contributors"
              >
                Web search ↗
              </a>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-3 pt-3 border-t border-dashed border-[hsl(var(--rule))] text-[11px] text-[hsl(var(--ink-muted))] leading-snug">
        Measure X-Ray does not collect donor data itself — these links open the
        official Cal-Access &amp; FPPC pages so you can check for potential
        conflicts of interest directly at the source.
      </div>
    </section>
  );
}

function VoteCard({ kind, text, icon: Icon, color }) {
  return (
    <div
      className="paper-card p-5 rounded-sm"
      style={{ borderTop: `3px solid ${color}` }}
      data-testid={`vote-${kind}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <span className="font-mono text-xs font-bold uppercase tracking-widest" style={{ color }}>
          A {kind.toUpperCase()} vote
        </span>
      </div>
      <p className="text-sm leading-relaxed text-[hsl(var(--ink-2))]">
        {text || "Not clearly stated."}
      </p>
    </div>
  );
}

function ChangesTab({ report, legalActions }) {
  const comps = report.current_law_comparisons || [];
  return (
    <div className="space-y-5" data-testid="changes-tab">
      {legalActions && legalActions.length > 0 && (
        <section className="paper-card p-5 rounded-sm">
          <div className="section-label mb-3">Detected legal actions</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-[hsl(var(--ink-muted))] border-b border-[hsl(var(--rule))]">
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Target</th>
                  <th className="py-2">Section</th>
                </tr>
              </thead>
              <tbody>
                {legalActions.map((a, i) => (
                  <tr key={i} className="border-b border-[hsl(var(--rule))] last:border-0" data-testid={`action-${i}`}>
                    <td className="py-2 pr-3">
                      <span className="stamp stamp-implied">{a.action_type}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{a.target_law}</td>
                    <td className="py-2 font-mono text-xs">§ {a.target_section}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {comps.length === 0 ? (
        <section className="paper-card p-6 rounded-sm text-center">
          <Scale className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--ink-muted))]" />
          <div className="text-sm text-[hsl(var(--ink-muted))]">
            No amended code sections detected, or current law could not be fetched.
          </div>
        </section>
      ) : (
        comps.map((c, i) => (
          <section key={i} className="paper-card-elevated p-5 rounded-sm" data-testid={`comparison-${i}`}>
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="section-label mb-1">{c.action_type} · {c.code_name || c.code}</div>
                <div className="font-serif text-xl font-semibold">§ {c.section}</div>
              </div>
              {c.explanation?.classification && (
                <span className="stamp stamp-implied">{c.explanation.classification.replace(/_/g, " ")}</span>
              )}
            </div>

            {c.explanation?.summary && (
              <p className="text-[15px] leading-relaxed mb-4">{c.explanation.summary}</p>
            )}

            {c.current_law_status && c.current_law_status !== "fetched" && (
              <div className="text-xs text-[hsl(var(--accent-amber))] mb-3 font-mono uppercase tracking-wider">
                current law: {c.current_law_status}
              </div>
            )}

            <DiffView diff={c.diff} currentUrl={c.current_law_url} />

            {c.explanation?.key_changes && c.explanation.key_changes.length > 0 && (
              <div className="mt-4 pt-4 border-t border-dashed border-[hsl(var(--rule))]">
                <div className="section-label mb-2">Key changes</div>
                <ul className="space-y-2">
                  {c.explanation.key_changes.map((k, j) => (
                    <li key={j} className="text-sm">
                      <div className="mb-1">{k.change}</div>
                      {(k.quote_current || k.quote_proposed) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {k.quote_current && <Quote text={`Current: ${k.quote_current}`} />}
                          {k.quote_proposed && <Quote text={`Proposed: ${k.quote_proposed}`} />}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        ))
      )}
    </div>
  );
}

function HiddenTab({ report }) {
  const items = report.hidden_or_easy_to_miss || [];
  const flags = report.red_flags || [];

  return (
    <div className="space-y-5" data-testid="hidden-tab">
      {flags.length > 0 && (
        <section className="paper-card p-5 rounded-sm">
          <div className="section-label mb-3">Pattern detector — red flags in text</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {flags.map((f, i) => (
              <div key={i} className="border border-[hsl(var(--rule))] rounded-sm p-3" data-testid={`flag-${f.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-semibold text-sm">{f.label}</span>
                  <span className="text-[11px] font-mono text-[hsl(var(--ink-muted))]">×{f.count}</span>
                </div>
                {f.excerpts && f.excerpts[0] && (
                  <div className="quote-block text-[12px] mt-1">&ldquo;…{f.excerpts[0]}…&rdquo;</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="paper-card p-5 rounded-sm">
        <div className="section-label mb-3">Analyst-flagged items</div>
        {items.length === 0 ? (
          <div className="text-sm text-[hsl(var(--ink-muted))]">None identified.</div>
        ) : (
          <ul className="space-y-4">
            {items.map((h, i) => (
              <li key={i} className="pb-4 border-b border-dashed border-[hsl(var(--rule))] last:border-0 last:pb-0" data-testid={`hidden-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                  <div className="font-semibold text-[15px] flex-1">{h.issue}</div>
                  <ConfidenceStamp confidence={h.confidence || (h.inferred ? "strongly_implied" : "explicit")} inferred={h.inferred} />
                </div>
                {h.why_it_matters && (
                  <p className="text-sm text-[hsl(var(--ink-2))] mb-2">{h.why_it_matters}</p>
                )}
                {h.quote && <Quote text={h.quote} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PowerTab({ report }) {
  const gains = report.who_gains_power || [];
  const pays = report.who_pays_or_bears_burden || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="power-tab">
      <PowerList title="Who gains power" items={gains} tone="gain" />
      <PowerList title="Who pays / bears burden" items={pays} tone="pay" />
    </div>
  );
}

function PowerList({ title, items, tone }) {
  const borderColor = tone === "gain" ? "hsl(var(--accent-blue))" : "hsl(var(--accent-amber))";
  return (
    <section className="paper-card p-5 rounded-sm" style={{ borderTop: `3px solid ${borderColor}` }}>
      <div className="section-label mb-3" style={{ color: borderColor }}>{title}</div>
      {items.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-muted))]">Not clearly stated.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((p, i) => (
            <li key={i} className="pb-3 border-b border-dashed border-[hsl(var(--rule))] last:border-0 last:pb-0">
              <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                <div className="font-semibold text-[15px]">{p.actor}</div>
                <ConfidenceStamp
                  confidence={p.inferred ? "strongly_implied" : "explicit"}
                  inferred={p.inferred}
                />
              </div>
              {p.how && <div className="text-sm text-[hsl(var(--ink-2))] mb-1.5">{p.how}</div>}
              {p.quote && <Quote text={p.quote} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ConflictsTab({ measureDbId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    try {
      const d = await getConflicts(measureDbId, true, force);
      setData(d);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureDbId]);

  if (loading) {
    return (
      <div className="paper-card p-6 text-center text-sm text-[hsl(var(--ink-muted))]" data-testid="conflicts-loading">
        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
        Running conflict adjudication…
      </div>
    );
  }

  if (!data) {
    return <div className="paper-card p-6 text-sm text-[hsl(var(--ink-muted))]">Failed to load.</div>;
  }

  const conflicts = data.conflicts || [];
  const counts = data.type_counts || {};
  const stampMap = {
    direct_conflict: { cls: "stamp-uncertain", label: "Direct conflict" },
    minor_differences: { cls: "stamp-implied", label: "Minor differences" },
    compatible: { cls: "stamp-explicit", label: "Compatible" },
  };

  return (
    <div className="space-y-5" data-testid="conflicts-tab">
      <section className="paper-card-elevated p-5 rounded-sm" data-testid="conflict-summary">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="section-label">Conflict scan</div>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(true); }}
            disabled={refreshing}
            className="pill-btn pill-btn-ghost text-xs disabled:opacity-50"
            data-testid="conflicts-rerun"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            Re-adjudicate
          </button>
        </div>
        <div className="flex items-baseline gap-6 flex-wrap">
          <div>
            <div className="font-serif text-3xl font-semibold">{data.candidate_count}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">candidates</div>
          </div>
          {Object.entries(counts).map(([t, n]) => {
            const meta = stampMap[t] || { cls: "stamp-inferred", label: t };
            return (
              <div key={t}>
                <div className="font-serif text-3xl font-semibold">{n}</div>
                <div className={`stamp ${meta.cls} mt-1`} data-testid={`conflict-count-${t}`}>
                  {meta.label}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[hsl(var(--ink-muted))] mt-3">
          Candidates = measures touching the same code section. Same-election pairs are ranked first.
          California&rsquo;s general rule for conflicting same-election propositions: the measure with more affirmative votes prevails where the conflict is real.
        </p>
      </section>

      {conflicts.length === 0 && (
        <section className="paper-card p-6 rounded-sm text-center" data-testid="conflicts-empty">
          <ShieldAlert className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--ink-muted))]" />
          <div className="text-sm text-[hsl(var(--ink-muted))]">
            No other ingested measure targets the same legal sections. Ingest more measures to surface conflicts.
          </div>
        </section>
      )}

      {conflicts.map((c, i) => {
        const a = c.assessment || {};
        const meta = stampMap[a.conflict_type] || { cls: "stamp-inferred", label: a.conflict_type || "pending" };
        return (
          <section
            key={i}
            className="paper-card-elevated p-5 rounded-sm"
            style={{
              borderLeft: `3px solid ${
                a.conflict_type === "direct_conflict" ? "hsl(var(--stamp-red))" :
                a.conflict_type === "minor_differences" ? "hsl(var(--accent-blue))" :
                "hsl(var(--accent-green))"
              }`,
            }}
            data-testid={`conflict-${i}`}
          >
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="section-label mb-1">{c.entity_type || "target"}</div>
                <div className="font-serif text-xl font-semibold">{c.entity_label}</div>
                <div className="text-xs text-[hsl(var(--ink-muted))] mt-1 font-mono">
                  my action: <span className="font-semibold">{c.my_edge_type}</span>
                  {" · "}
                  other action: <span className="font-semibold">{c.other_edge_type}</span>
                  {c.same_election && (
                    <span className="ml-2 stamp stamp-uncertain">same election</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`stamp ${meta.cls}`} data-testid={`conflict-type-${i}`}>{meta.label}</span>
                {a.confidence && <ConfidenceStamp confidence={a.confidence} inferred={false} />}
              </div>
            </div>

            <div className="mb-3 text-xs text-[hsl(var(--ink-muted))]">
              vs{" "}
              <Link
                to={`/measures/${c.other_measure.id}`}
                className="font-mono text-[hsl(var(--accent-blue))] hover:underline"
                data-testid={`conflict-other-${i}`}
              >
                {c.other_measure.title || c.other_measure.measure_id}
              </Link>
            </div>

            {a.summary && (
              <p className="text-[15px] leading-relaxed mb-4">{a.summary}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <div className="section-label mb-1">This measure</div>
                <Quote text={a.quote_a || c.my_quote} />
              </div>
              <div>
                <div className="section-label mb-1">{c.other_measure.measure_id}</div>
                <Quote text={a.quote_b || c.other_quote} />
              </div>
            </div>

            {a.governing_rule_note && (
              <div className="text-xs text-[hsl(var(--ink-muted))] pt-3 border-t border-dashed border-[hsl(var(--rule))]">
                <span className="section-label">Governing rule · </span>
                {a.governing_rule_note}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MoneyTab({ measureDbId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addUrl, setAddUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const d = await getFinance(measureDbId);
      setData(d);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureDbId]);

  const submitUrl = async (e) => {
    e.preventDefault();
    const url = addUrl.trim();
    if (!url) return;
    setAdding(true);
    try {
      const res = await ingestFinance(measureDbId, [url]);
      if (res.errors && res.errors.length > 0) {
        toast.error(res.errors[0]);
      } else {
        toast.success("Finance source added");
      }
      setAddUrl("");
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to ingest finance source");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="paper-card p-6 text-center text-sm text-[hsl(var(--ink-muted))]" data-testid="money-loading">
        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
        Loading finance data…
      </div>
    );
  }

  const populated = data && data.status === "populated";

  return (
    <div className="space-y-5" data-testid="money-tab">
      <section className="paper-card-elevated p-5 rounded-sm" data-testid="finance-add">
        <div className="section-label mb-2">Add finance source</div>
        <p className="text-xs text-[hsl(var(--ink-muted))] mb-3">
          Paste an FPPC top-10-contributors URL or CAL-ACCESS committee URL.
          We fetch the page, extract structured rows, and separate verified numbers from narrative.
        </p>
        <form onSubmit={submitUrl} className="flex gap-2 flex-col sm:flex-row">
          <input
            value={addUrl}
            onChange={(e) => setAddUrl(e.target.value)}
            placeholder="https://www.fppc.ca.gov/search-filings/top-10-contributors-list/..."
            className="flex-1 bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
            data-testid="finance-url-input"
          />
          <button
            type="submit"
            disabled={adding}
            className="pill-btn pill-btn-primary disabled:opacity-50 justify-center"
            data-testid="finance-submit"
          >
            {adding ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Ingesting…
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Add
              </>
            )}
          </button>
        </form>
      </section>

      {!populated && (
        <section className="paper-card p-6 rounded-sm text-center" data-testid="finance-empty">
          <DollarSign className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--ink-muted))]" />
          <div className="text-sm text-[hsl(var(--ink-muted))]">
            {data?.note || "No finance data ingested yet."}
          </div>
        </section>
      )}

      {populated && (
        <>
          <section className="paper-card-elevated p-5 rounded-sm" data-testid="finance-totals">
            <div className="section-label mb-3">
              Totals {data.measure_label_in_source ? `· ${data.measure_label_in_source}` : ""}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="paper-card p-4 rounded-sm" style={{ borderTop: "3px solid hsl(var(--accent-green))" }}>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[hsl(var(--accent-green))] mb-1">
                  Support raised
                </div>
                <div className="font-serif text-3xl font-semibold">{data.support_total || "—"}</div>
                {data.support_committees?.length > 0 && (
                  <div className="text-xs text-[hsl(var(--ink-muted))] mt-2 leading-snug">
                    {data.support_committees.slice(0, 2).join(" · ")}
                  </div>
                )}
              </div>
              <div className="paper-card p-4 rounded-sm" style={{ borderTop: "3px solid hsl(var(--stamp-red))" }}>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[hsl(var(--stamp-red))] mb-1">
                  Oppose raised
                </div>
                <div className="font-serif text-3xl font-semibold">{data.oppose_total || "—"}</div>
                {data.oppose_committees?.length > 0 && (
                  <div className="text-xs text-[hsl(var(--ink-muted))] mt-2 leading-snug">
                    {data.oppose_committees.slice(0, 2).join(" · ")}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
              Data completeness:{" "}
              <span className={`stamp ${
                data.data_completeness === "complete" ? "stamp-explicit" :
                data.data_completeness === "partial" ? "stamp-implied" : "stamp-uncertain"
              }`} data-testid="finance-completeness">
                {data.data_completeness || "none"}
              </span>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ContributorList
              tone="support"
              title="Top contributors — support"
              items={data.top_contributors_support || []}
            />
            <ContributorList
              tone="oppose"
              title="Top contributors — oppose"
              items={data.top_contributors_oppose || []}
            />
          </div>

          {(data.caveats?.length > 0 || data.inferred_notes?.length > 0) && (
            <section className="paper-card p-5 rounded-sm border-l-4 border-[hsl(var(--accent-amber))]" data-testid="finance-caveats">
              <div className="section-label mb-3 text-[hsl(var(--accent-amber))]">Caveats &amp; inferences</div>
              {data.caveats?.length > 0 && (
                <>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mb-1.5">Verified caveats (from page)</div>
                  <ul className="space-y-1.5 text-sm text-[hsl(var(--ink-2))] mb-4">
                    {data.caveats.map((c, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[hsl(var(--accent-amber))] shrink-0">—</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {data.inferred_notes?.length > 0 && (
                <>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mb-1.5">Inferred narrative (not directly quoted)</div>
                  <ul className="space-y-1.5 text-sm text-[hsl(var(--ink-muted))]">
                    {data.inferred_notes.map((n, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="shrink-0">—</span>
                        <span className="italic">{n}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}

          {data.sources?.length > 0 && (
            <section className="paper-card p-5 rounded-sm" data-testid="finance-sources">
              <div className="section-label mb-3">Source pages</div>
              <ul className="space-y-1.5 text-sm">
                {data.sources.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 font-mono text-xs">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[hsl(var(--accent-blue))] hover:underline truncate"
                    >
                      {s.url}
                    </a>
                    <span className="stamp stamp-inferred shrink-0">{s.data_completeness}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function ContributorList({ title, tone, items }) {
  const color = tone === "support" ? "hsl(var(--accent-green))" : "hsl(var(--stamp-red))";
  return (
    <section
      className="paper-card p-5 rounded-sm"
      style={{ borderTop: `3px solid ${color}` }}
      data-testid={`contributors-${tone}`}
    >
      <div className="section-label mb-3" style={{ color }}>{title}</div>
      {items.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-muted))]">None reported.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((c, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-3 py-1.5 border-b border-dashed border-[hsl(var(--rule))] last:border-0"
              data-testid={`contributor-${tone}-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.name || "(unknown)"}</div>
                {c.committee && (
                  <div className="text-[11px] font-mono text-[hsl(var(--ink-muted))] truncate">
                    via {c.committee}
                  </div>
                )}
              </div>
              <div className="font-mono font-semibold text-sm shrink-0" style={{ color }}>
                {c.amount || "—"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function LinkagesTab({ report, measureDbId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getLinkages(measureDbId)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [measureDbId]);

  if (loading) {
    return (
      <div className="paper-card p-6 text-center text-sm text-[hsl(var(--ink-muted))]" data-testid="linkages-loading">
        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
        Loading linkage graph…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="paper-card p-6 text-sm text-[hsl(var(--ink-muted))]">Failed to load linkages.</div>
    );
  }

  const hiddenLinkages = data.hidden_linkages || [];
  const entities = data.entities || [];
  const overlapEntities = entities.filter((e) => e.overlap_count > 0);
  const byType = data.by_type || {};
  const typeCounts = Object.entries(byType).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-5" data-testid="linkages-tab">
      {/* Summary bar */}
      <section className="paper-card-elevated p-5 rounded-sm" data-testid="linkage-summary">
        <div className="section-label mb-3">Linkage graph</div>
        <div className="flex items-baseline gap-6 flex-wrap">
          <div>
            <div className="font-serif text-3xl font-semibold">{data.entity_count}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">entities referenced</div>
          </div>
          <div>
            <div className="font-serif text-3xl font-semibold text-[hsl(var(--accent-blue))]">
              {data.total_cross_measure_overlaps}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">cross-measure overlaps</div>
          </div>
          <div>
            <div className="font-serif text-3xl font-semibold text-[hsl(var(--accent-amber))]">
              {hiddenLinkages.length}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">hidden linkages (LLM)</div>
          </div>
          <div className="flex gap-2 ml-auto flex-wrap">
            {typeCounts.map(([t, n]) => (
              <span key={t} className="stamp stamp-inferred" data-testid={`type-count-${t}`}>
                {t} · {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-measure overlaps — the shared graph payoff */}
      {overlapEntities.length > 0 && (
        <section className="paper-card p-5 rounded-sm border-l-4 border-[hsl(var(--accent-blue))]" data-testid="overlaps-panel">
          <div className="section-label mb-3 text-[hsl(var(--accent-blue))]">
            Shared with other measures
          </div>
          <ul className="space-y-4">
            {overlapEntities.map((e) => (
              <li key={e.id} className="pb-3 border-b border-dashed border-[hsl(var(--rule))] last:border-0" data-testid={`overlap-${e.id}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="stamp stamp-inferred">{e.type}</span>
                  <span className="font-mono text-sm font-semibold">{e.label}</span>
                  <span className="text-[11px] font-mono text-[hsl(var(--ink-muted))]">
                    · {e.overlap_count} other measure{e.overlap_count !== 1 ? "s" : ""}
                  </span>
                </div>
                <ul className="ml-0 mt-2 space-y-1.5">
                  {e.overlaps.map((o, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <span className="text-[hsl(var(--accent-blue))] mt-0.5 shrink-0">↳</span>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/measures/${o.measure_id}`}
                          data-testid={`overlap-link-${i}`}
                          className="font-medium text-[hsl(var(--accent-blue))] hover:underline break-words"
                        >
                          {o.measure_title || o.measure_label}
                        </Link>
                        <span className="ml-2 stamp stamp-implied">{o.edge_type}</span>
                        {o.quote && (
                          <div className="text-[12px] text-[hsl(var(--ink-muted))] font-mono mt-0.5 line-clamp-2">
                            “{o.quote}”
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* All entities grouped by type */}
      <section className="paper-card p-5 rounded-sm" data-testid="entities-panel">
        <div className="section-label mb-3">Entities referenced</div>
        {entities.length === 0 ? (
          <div className="text-sm text-[hsl(var(--ink-muted))]">None detected.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {entities.map((e) => (
              <div
                key={e.id}
                data-testid={`entity-${e.id}`}
                className={`border rounded-sm p-2.5 ${
                  e.overlap_count > 0
                    ? "border-[hsl(var(--accent-blue))] bg-[hsl(var(--accent-blue)/0.04)]"
                    : "border-[hsl(var(--rule))]"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                    {e.type}
                  </span>
                  {e.edge_types.map((et) => (
                    <span
                      key={et}
                      className={`text-[10px] font-mono px-1 py-px rounded-sm ${
                        ["amend", "add", "repeal"].includes(et)
                          ? "bg-[hsl(var(--stamp-red)/0.1)] text-[hsl(var(--stamp-red))]"
                          : "bg-[hsl(var(--paper-2))] text-[hsl(var(--ink-muted))]"
                      }`}
                    >
                      {et}
                    </span>
                  ))}
                </div>
                <div className="text-sm font-medium leading-snug break-words">{e.label}</div>
                {e.overlap_count > 0 && (
                  <div className="text-[11px] font-mono text-[hsl(var(--accent-blue))] mt-1">
                    also in {e.overlap_count} other measure{e.overlap_count !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Hidden LLM linkages */}
      <section className="paper-card p-5 rounded-sm" data-testid="hidden-linkages-panel">
        <div className="section-label mb-3">Hidden linkages (inferred by analyst)</div>
        {hiddenLinkages.length === 0 ? (
          <div className="text-sm text-[hsl(var(--ink-muted))]">No indirect dependencies identified.</div>
        ) : (
          <ul className="space-y-4">
            {hiddenLinkages.map((l, i) => (
              <li
                key={i}
                data-testid={`hidden-linkage-${i}`}
                className="pb-4 border-b border-dashed border-[hsl(var(--rule))] last:border-0 last:pb-0"
              >
                <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                  <div className="font-semibold text-[15px] flex-1">{l.linkage}</div>
                  <ConfidenceStamp
                    confidence={l.confidence || "plausible_uncertain"}
                    inferred={l.inferred}
                  />
                </div>
                {l.why_it_matters && (
                  <p className="text-sm text-[hsl(var(--ink-2))] mb-2">{l.why_it_matters}</p>
                )}
                {l.dependency_path && l.dependency_path.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap mb-2">
                    {l.dependency_path.map((step, j) => (
                      <React.Fragment key={j}>
                        <span className="text-[11px] font-mono px-1.5 py-0.5 bg-[hsl(var(--paper-2))] border border-[hsl(var(--rule))] rounded-sm">
                          {step}
                        </span>
                        {j < l.dependency_path.length - 1 && (
                          <span className="text-[hsl(var(--ink-muted))] text-xs">→</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {l.quote && <Quote text={l.quote} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function EvidenceTab({ findings, sources }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(() => {
    if (filter === "all") return findings;
    return findings.filter((f) => f.category === filter);
  }, [findings, filter]);

  const cats = ["all", ...Array.from(new Set(findings.map((f) => f.category)))];

  return (
    <div className="space-y-5" data-testid="evidence-tab">
      <section className="paper-card p-5 rounded-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="section-label">Evidence table ({filtered.length})</div>
          <div className="flex gap-1 flex-wrap">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c}`}
                className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm transition-colors ${
                  filter === c
                    ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))]"
                    : "border border-[hsl(var(--rule))] text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm text-[hsl(var(--ink-muted))]">No findings.</div>
        ) : (
          <ul className="space-y-4">
            {filtered.map((f, i) => (
              <li key={i} className="pb-4 border-b border-dashed border-[hsl(var(--rule))] last:border-0 last:pb-0" data-testid={`finding-${i}`}>
                <div className="flex items-start gap-2 mb-2 flex-wrap">
                  <span className="stamp stamp-inferred shrink-0">{f.category}</span>
                  <div className="flex-1 font-medium text-[15px]">{f.statement}</div>
                  <ConfidenceStamp confidence={f.confidence} inferred={f.inferred} />
                </div>
                {f.quote && <Quote text={f.quote} />}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="paper-card p-5 rounded-sm">
        <div className="section-label mb-3">Source documents ({sources.length})</div>
        <ul className="space-y-2">
          {sources.map((s, i) => (
            <li key={i} className="flex items-start justify-between gap-3 py-2 border-b border-dashed border-[hsl(var(--rule))] last:border-0" data-testid={`source-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="stamp stamp-inferred">{s.source_type}</span>
                  {s.official_flag && <span className="stamp stamp-explicit">official</span>}
                </div>
                <div className="font-medium text-sm truncate">{s.title}</div>
                <div className="text-[11px] font-mono text-[hsl(var(--ink-muted))] truncate">{s.url}</div>
              </div>
              {s.url && !s.url.startsWith("manual://") && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[hsl(var(--accent-blue))] shrink-0"
                  aria-label="Open source"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
